import { useEffect, useRef } from "react";
import "./styles.css";

type CoinType = "manyu" | "eth" | "cat" | "rh" | "bomb";

type Coin = {
  id: number;
  type: CoinType;
  img: HTMLImageElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  sliced: boolean;
};

type SlashPoint = {
  x: number;
  y: number;
  time: number;
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  bomb: boolean;
};

type HitFlash = {
  x: number;
  y: number;
  life: number;
  bomb: boolean;
};

type ScorePop = {
  x: number;
  y: number;
  life: number;
  value: number;
};

type ComboPopup = {
  count: number;
  text: string;
  life: number;
};

const IMAGE_PATHS: Record<CoinType, string> = {
  manyu: "/manyucoin.png",
  eth: "/ethcoin.png",
  cat: "/catcoin.png",
  rh: "/rhcoin.png",
  bomb: "/bombcoin.png",
};

export default function App() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
const canvasElement = canvasRef.current;
if (!canvasElement) return;

const context = canvasElement.getContext("2d");
if (!context) return;

const canvas: HTMLCanvasElement = canvasElement;
const ctx: CanvasRenderingContext2D = context;

    let animationId = 0;
    let lastTime = performance.now();

    let spawnTimer = 0;
    let nextId = 0;

    let score = 0;
    let level = 1;
    let gameOver = false;

    let pointerDown = false;

    let levelFlash = 0;
    let bombFlash = 0;

    let introTime = 0;
    let gameStarted = false;

    let currentSwipeHits = 0;
    let comboPopup: ComboPopup | null = null;

    let winkTime = 0;

    const coins: Coin[] = [];
    const slashPoints: SlashPoint[] = [];
    const particles: Particle[] = [];
    const hitFlashes: HitFlash[] = [];
    const scorePops: ScorePop[] = [];

    const background = new Image();
    background.src = "/background1.png";

    const ninjaCatWink = new Image();
    ninjaCatWink.src = "/ninjacat-wink.png";

    const images = {} as Record<CoinType, HTMLImageElement>;

    Object.entries(IMAGE_PATHS).forEach(([key, src]) => {
      const img = new Image();
      img.src = src;
      images[key as CoinType] = img;
    });

    function resizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    function restartGame() {
      score = 0;
      level = 1;
      gameOver = false;

      spawnTimer = 0;
      nextId = 0;

      pointerDown = false;

      levelFlash = 0;
      bombFlash = 0;

      introTime = 0;
      gameStarted = false;

      currentSwipeHits = 0;
      comboPopup = null;
      winkTime = 0;

      coins.length = 0;
      slashPoints.length = 0;
      particles.length = 0;
      hitFlashes.length = 0;
      scorePops.length = 0;

      lastTime = performance.now();
    }

    function chooseCoinType(): CoinType {
      const goodTypes: CoinType[] = [
        "manyu",
        "eth",
        "cat",
        "rh",
      ];

      const bombChance =
        score >= 5
          ? Math.min(0.07 + level * 0.012, 0.18)
          : 0;

      if (Math.random() < bombChance) {
        return "bomb";
      }

      return goodTypes[
        Math.floor(Math.random() * goodTypes.length)
      ];
    }

    function spawnCoin() {
      if (gameOver || !gameStarted) return;

      const width = window.innerWidth;
      const height = window.innerHeight;

      const type = chooseCoinType();

      const size = Math.min(
        Math.max(width * 0.085, 72),
        105
      );

      const x =
        size / 2 +
        Math.random() * (width - size);

      let vx =
        (Math.random() - 0.5) *
        (240 + level * 16);

      if (x < width * 0.33) {
        vx = Math.abs(vx) + 55;
      }

      if (x > width * 0.67) {
        vx = -Math.abs(vx) - 55;
      }

      const launchStrength =
        height *
          (1.28 + Math.random() * 0.22) +
        level * 15;

      coins.push({
        id: nextId++,
        type,
        img: images[type],
        x,
        y: height + size * 0.55,
        vx,
        vy: -launchStrength,
        size,
        rotation:
          Math.random() * Math.PI * 2,
        rotationSpeed:
          (Math.random() - 0.5) *
          (4.5 + level * 0.12),
        sliced: false,
      });
    }

    function spawnWave() {
      if (gameOver || !gameStarted) return;

      let amount = 1;

      if (level >= 2 && Math.random() < 0.35) {
        amount = 2;
      }

      if (level >= 3 && Math.random() < 0.22) {
        amount = 3;
      }

      if (level >= 5 && Math.random() < 0.12) {
        amount = 4;
      }

      if (level >= 8 && Math.random() < 0.08) {
        amount = 5;
      }

      for (let i = 0; i < amount; i++) {
        spawnCoin();
      }
    }

    function getPointerPosition(event: PointerEvent) {
      const rect = canvas.getBoundingClientRect();

      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    }

    function distancePointToSegment(
      px: number,
      py: number,
      x1: number,
      y1: number,
      x2: number,
      y2: number
    ) {
      const dx = x2 - x1;
      const dy = y2 - y1;

      if (dx === 0 && dy === 0) {
        return Math.hypot(px - x1, py - y1);
      }

      const t = Math.max(
        0,
        Math.min(
          1,
          ((px - x1) * dx +
            (py - y1) * dy) /
            (dx * dx + dy * dy)
        )
      );

      const closestX = x1 + t * dx;
      const closestY = y1 + t * dy;

      return Math.hypot(
        px - closestX,
        py - closestY
      );
    }

    function getComboText(count: number) {
      if (count === 2) return "DOUBLE SLASH!";
      if (count === 3) return "TRIPLE SLASH!";
      if (count === 4) return "COMBO x4!";
      if (count >= 5) return `NINJA COMBO x${count}!`;

      return "";
    }

    function createHitEffect(
      x: number,
      y: number,
      bomb: boolean,
      points: number
    ) {
      hitFlashes.push({
        x,
        y,
        life: 1,
        bomb,
      });

      const amount = bomb ? 28 : 16;

      for (let i = 0; i < amount; i++) {
        const angle =
          Math.random() * Math.PI * 2;

        const speed =
          80 + Math.random() * 260;

        particles.push({
          x,
          y,
          vx:
            Math.cos(angle) * speed,
          vy:
            Math.sin(angle) * speed,
          life: 1,
          maxLife:
            0.25 + Math.random() * 0.3,
          size:
            2 + Math.random() * 4,
          bomb,
        });
      }

      if (!bomb) {
        scorePops.push({
          x,
          y,
          life: 1,
          value: points,
        });
      }
    }

    function updateLevel() {
      const newLevel =
        Math.floor(score / 10) + 1;

      if (newLevel > level) {
        level = newLevel;
        levelFlash = 1;
      }
    }

    function checkSlashHits() {
      if (
        slashPoints.length < 2 ||
        gameOver ||
        !gameStarted
      ) {
        return;
      }

      const p1 =
        slashPoints[
          slashPoints.length - 2
        ];

      const p2 =
        slashPoints[
          slashPoints.length - 1
        ];

      for (const coin of coins) {
        if (coin.sliced) continue;

        const distance =
          distancePointToSegment(
            coin.x,
            coin.y,
            p1.x,
            p1.y,
            p2.x,
            p2.y
          );

        if (distance < coin.size * 0.45) {
          coin.sliced = true;

          if (coin.type === "bomb") {
            createHitEffect(
              coin.x,
              coin.y,
              true,
              0
            );

            bombFlash = 1;
            gameOver = true;

            return;
          }

          currentSwipeHits += 1;

          const points =
            currentSwipeHits >= 2 ? 2 : 1;

          score += points;

          createHitEffect(
            coin.x,
            coin.y,
            false,
            points
          );

          if (currentSwipeHits >= 2) {
            comboPopup = {
              count: currentSwipeHits,
              text: getComboText(currentSwipeHits),
              life: 1,
            };
          }

          if (currentSwipeHits >= 5) {
            winkTime = 1;
          }

          updateLevel();
        }
      }
    }

    function getRestartButton() {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const buttonWidth = Math.min(width * 0.42, 220);
      const buttonHeight = 58;

      return {
        x: width / 2 - buttonWidth / 2,
        y: height * 0.57,
        width: buttonWidth,
        height: buttonHeight,
      };
    }

    function isInsideRestartButton(
      x: number,
      y: number
    ) {
      const button = getRestartButton();

      return (
        x >= button.x &&
        x <= button.x + button.width &&
        y >= button.y &&
        y <= button.y + button.height
      );
    }

    function handlePointerDown(
      event: PointerEvent
    ) {
      const pos =
        getPointerPosition(event);

      if (gameOver) {
        if (
          isInsideRestartButton(
            pos.x,
            pos.y
          )
        ) {
          restartGame();
        }

        return;
      }

      if (!gameStarted) return;

      pointerDown = true;
      currentSwipeHits = 0;

      canvas.setPointerCapture(
        event.pointerId
      );

      slashPoints.length = 0;

      slashPoints.push({
        x: pos.x,
        y: pos.y,
        time: performance.now(),
      });
    }

    function handlePointerMove(
      event: PointerEvent
    ) {
      if (
        !pointerDown ||
        gameOver ||
        !gameStarted
      ) {
        return;
      }

      const pos =
        getPointerPosition(event);

      slashPoints.push({
        x: pos.x,
        y: pos.y,
        time: performance.now(),
      });

      if (slashPoints.length > 18) {
        slashPoints.shift();
      }

      checkSlashHits();
    }

    function handlePointerUp() {
      pointerDown = false;
      currentSwipeHits = 0;
    }

    canvas.addEventListener(
      "pointerdown",
      handlePointerDown
    );

    canvas.addEventListener(
      "pointermove",
      handlePointerMove
    );

    canvas.addEventListener(
      "pointerup",
      handlePointerUp
    );

    canvas.addEventListener(
      "pointercancel",
      handlePointerUp
    );

    function drawBackground() {
      const width = window.innerWidth;
      const height = window.innerHeight;

      if (
        background.complete &&
        background.width > 0
      ) {
        const imageRatio =
          background.width /
          background.height;

        const screenRatio =
          width / height;

        let drawWidth = width;
        let drawHeight = height;

        let drawX = 0;
        let drawY = 0;

        if (imageRatio > screenRatio) {
          drawHeight = height;
          drawWidth =
            height * imageRatio;
          drawX =
            (width - drawWidth) / 2;
        } else {
          drawWidth = width;
          drawHeight =
            width / imageRatio;
          drawY =
            (height - drawHeight) / 2;
        }

        ctx.drawImage(
          background,
          drawX,
          drawY,
          drawWidth,
          drawHeight
        );
      } else {
        ctx.fillStyle = "#130000";

        ctx.fillRect(
          0,
          0,
          width,
          height
        );
      }

      ctx.fillStyle =
        "rgba(0,0,0,0.12)";

      ctx.fillRect(
        0,
        0,
        width,
        height
      );
    }

    function drawCoins() {
      for (const coin of coins) {
        if (coin.sliced) continue;

        if (
          !coin.img.complete ||
          coin.img.width === 0
        ) {
          continue;
        }

        ctx.save();

        ctx.translate(
          coin.x,
          coin.y
        );

        ctx.rotate(
          coin.rotation
        );

        ctx.shadowColor =
          coin.type === "bomb"
            ? "rgba(255,40,20,0.8)"
            : "rgba(255,210,80,0.5)";

        ctx.shadowBlur =
          coin.type === "bomb"
            ? 18
            : 12;

        ctx.drawImage(
          coin.img,
          -coin.size / 2,
          -coin.size / 2,
          coin.size,
          coin.size
        );

        ctx.restore();
      }
    }

    function drawParticles() {
      for (const p of particles) {
        const alpha = p.life;

        ctx.save();

        if (p.bomb) {
          ctx.fillStyle =
            `rgba(255,${
              70 + Math.random() * 100
            },10,${alpha})`;

          ctx.shadowColor =
            "#ff3300";
        } else {
          ctx.fillStyle =
            `rgba(100,255,130,${alpha})`;

          ctx.shadowColor =
            "#34ff65";
        }

        ctx.shadowBlur = 10;

        ctx.beginPath();

        ctx.arc(
          p.x,
          p.y,
          p.size,
          0,
          Math.PI * 2
        );

        ctx.fill();

        ctx.restore();
      }
    }

    function drawHitFlashes() {
      for (const flash of hitFlashes) {
        const radius =
          10 +
          (1 - flash.life) * 55;

        ctx.save();

        ctx.globalAlpha =
          flash.life;

        ctx.strokeStyle =
          flash.bomb
            ? "#ff3b19"
            : "#ffffff";

        ctx.lineWidth = 5;

        ctx.shadowColor =
          flash.bomb
            ? "#ff2200"
            : "#55ff88";

        ctx.shadowBlur = 25;

        ctx.beginPath();

        ctx.arc(
          flash.x,
          flash.y,
          radius,
          0,
          Math.PI * 2
        );

        ctx.stroke();

        ctx.restore();
      }
    }

    function drawScorePops() {
      ctx.save();

      ctx.textAlign = "center";

      for (const pop of scorePops) {
        ctx.globalAlpha =
          Math.max(pop.life, 0);

        ctx.fillStyle =
          pop.value > 1
            ? "#ffe56b"
            : "#ffffff";

        ctx.shadowColor =
          pop.value > 1
            ? "#ffbd00"
            : "#39ff6f";

        ctx.shadowBlur = 12;

        ctx.font =
          "900 25px Arial";

        ctx.fillText(
          `+${pop.value}`,
          pop.x,
          pop.y -
            (1 - pop.life) * 45
        );
      }

      ctx.restore();
    }

    function drawCombo() {
      if (!comboPopup) return;

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      const life =
        Math.max(comboPopup.life, 0);

      const scale =
        1 +
        Math.max(
          0,
          comboPopup.life - 0.72
        ) * 0.45;

      ctx.save();

      ctx.globalAlpha =
        Math.min(life * 1.4, 1);

      ctx.translate(
        width / 2,
        height * 0.29
      );

      ctx.scale(scale, scale);

      ctx.textAlign = "center";

      const bigCombo =
        comboPopup.count >= 5;

      ctx.font =
        `900 ${Math.min(
          width * 0.075,
          bigCombo ? 58 : 48
        )}px Arial`;

      ctx.fillStyle =
        bigCombo
          ? "#79ff91"
          : "#ffffff";

      ctx.shadowColor =
        bigCombo
          ? "#25ff5d"
          : "#ffca32";

      ctx.shadowBlur =
        bigCombo ? 28 : 18;

      ctx.fillText(
        comboPopup.text,
        0,
        0
      );

      if (comboPopup.count >= 4) {
        ctx.font =
          "900 22px Arial";

        ctx.fillStyle =
          "#ffe26a";

        ctx.shadowColor =
          "#ffb000";

        ctx.shadowBlur = 12;

        ctx.fillText(
          "BONUS!",
          0,
          34
        );
      }

      ctx.restore();
    }

    function drawNinjaCatWink() {
      if (
        winkTime <= 0 ||
        !ninjaCatWink.complete ||
        ninjaCatWink.width === 0
      ) {
        return;
      }

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      const catWidth =
        Math.min(
          width * 0.3,
          240
        );

      const ratio =
        ninjaCatWink.height /
        ninjaCatWink.width;

      const catHeight =
        catWidth * ratio;

      const alpha =
        Math.min(
          winkTime * 2.5,
          1
        );

      ctx.save();

      ctx.globalAlpha = alpha;

      ctx.shadowColor =
        "#3cff70";

      ctx.shadowBlur = 18;

      ctx.drawImage(
        ninjaCatWink,
        width / 2 -
          catWidth / 2,
        height -
          catHeight * 0.78,
        catWidth,
        catHeight
      );

      ctx.restore();
    }

    function drawSlash(now: number) {
      while (
        slashPoints.length > 0 &&
        now - slashPoints[0].time > 160
      ) {
        slashPoints.shift();
      }

      if (slashPoints.length < 2) {
        return;
      }

      ctx.save();

      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.beginPath();

      ctx.moveTo(
        slashPoints[0].x,
        slashPoints[0].y
      );

      for (
        let i = 1;
        i < slashPoints.length;
        i++
      ) {
        ctx.lineTo(
          slashPoints[i].x,
          slashPoints[i].y
        );
      }

      ctx.strokeStyle =
        "rgba(30,255,90,0.22)";

      ctx.lineWidth = 22;

      ctx.shadowColor =
        "#29ff65";

      ctx.shadowBlur = 24;

      ctx.stroke();

      ctx.beginPath();

      ctx.moveTo(
        slashPoints[0].x,
        slashPoints[0].y
      );

      for (
        let i = 1;
        i < slashPoints.length;
        i++
      ) {
        ctx.lineTo(
          slashPoints[i].x,
          slashPoints[i].y
        );
      }

      ctx.strokeStyle =
        "#54ff7b";

      ctx.lineWidth = 8;

      ctx.shadowColor =
        "#39ff6f";

      ctx.shadowBlur = 15;

      ctx.stroke();

      ctx.beginPath();

      ctx.moveTo(
        slashPoints[0].x,
        slashPoints[0].y
      );

      for (
        let i = 1;
        i < slashPoints.length;
        i++
      ) {
        ctx.lineTo(
          slashPoints[i].x,
          slashPoints[i].y
        );
      }

      ctx.strokeStyle =
        "rgba(245,255,247,0.98)";

      ctx.lineWidth = 2.5;

      ctx.shadowBlur = 0;

      ctx.stroke();

      ctx.restore();
    }

    function drawHUD() {
      if (!gameStarted) return;

      const width =
        window.innerWidth;

      ctx.save();

      ctx.fillStyle =
        "#ffffff";

      ctx.shadowColor =
        "#000000";

      ctx.shadowBlur = 6;

      ctx.font =
        "900 20px Arial";

      ctx.textAlign = "left";

      ctx.fillText(
        `SCORE ${score}`,
        18,
        32
      );

      ctx.textAlign = "right";

      ctx.fillText(
        `LEVEL ${level}`,
        width - 18,
        32
      );

      ctx.restore();
    }

    function drawGameTitle() {
      if (!gameStarted || gameOver) return;

      const width =
        window.innerWidth;

      ctx.save();

      ctx.textAlign = "center";

      ctx.font =
        "900 18px Arial";

      ctx.fillStyle =
        "#ffffff";

      ctx.shadowColor =
        "#29ff65";

      ctx.shadowBlur = 10;

      ctx.fillText(
        "CRYPTO NINJA",
        width / 2,
        31
      );

      ctx.restore();
    }

    function drawIntro() {
      if (gameStarted) return;

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      ctx.save();

      ctx.textAlign = "center";

      const pulse =
        1 +
        Math.sin(introTime * 6) * 0.03;

      ctx.translate(
        width / 2,
        height * 0.36
      );

      ctx.scale(
        pulse,
        pulse
      );

      ctx.font =
        `900 ${Math.min(
          width * 0.1,
          72
        )}px Arial`;

      ctx.fillStyle =
        "#ffffff";

      ctx.shadowColor =
        "#ffb000";

      ctx.shadowBlur = 16;

      ctx.fillText(
        "CRYPTO",
        0,
        0
      );

      ctx.font =
        `900 ${Math.min(
          width * 0.13,
          90
        )}px Arial`;

      ctx.fillStyle =
        "#52ff7f";

      ctx.shadowColor =
        "#27ff63";

      ctx.shadowBlur = 28;

      ctx.fillText(
        "NINJA",
        0,
        Math.min(
          width * 0.11,
          82
        )
      );

      ctx.restore();

      if (introTime > 0.65) {
        ctx.save();

        ctx.textAlign =
          "center";

        ctx.font =
          "900 24px Arial";

        ctx.fillStyle =
          "#ffffff";

        ctx.shadowColor =
          "#000000";

        ctx.shadowBlur = 8;

        let message =
          "GET READY";

        if (introTime > 1.75) {
          message = "GO!";

          ctx.fillStyle =
            "#61ff85";

          ctx.shadowColor =
            "#29ff65";

          ctx.shadowBlur = 20;
        }

        ctx.fillText(
          message,
          width / 2,
          height * 0.58
        );

        ctx.restore();
      }
    }

    function drawLevelFlash() {
      if (levelFlash <= 0) {
        return;
      }

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      ctx.save();

      const alpha =
        Math.min(
          levelFlash,
          0.7
        );

      const gradient =
        ctx.createRadialGradient(
          width / 2,
          height / 2,
          height * 0.15,
          width / 2,
          height / 2,
          height * 0.7
        );

      gradient.addColorStop(
        0,
        "rgba(0,255,80,0)"
      );

      gradient.addColorStop(
        1,
        `rgba(20,255,80,${
          alpha * 0.45
        })`
      );

      ctx.fillStyle =
        gradient;

      ctx.fillRect(
        0,
        0,
        width,
        height
      );

      ctx.globalAlpha =
        levelFlash;

      ctx.textAlign =
        "center";

      ctx.font =
        `900 ${Math.min(
          width * 0.09,
          60
        )}px Arial`;

      ctx.fillStyle =
        "#ffffff";

      ctx.shadowColor =
        "#32ff67";

      ctx.shadowBlur = 30;

      ctx.fillText(
        `LEVEL ${level}`,
        width / 2,
        height * 0.43
      );

      ctx.restore();
    }

    function drawBombFlash() {
      if (bombFlash <= 0) {
        return;
      }

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      ctx.save();

      ctx.globalAlpha =
        bombFlash * 0.65;

      ctx.fillStyle =
        "#ff1800";

      ctx.fillRect(
        0,
        0,
        width,
        height
      );

      ctx.restore();
    }

    function drawGameOver() {
      if (!gameOver) return;

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      const button =
        getRestartButton();

      ctx.save();

      ctx.fillStyle =
        "rgba(60,0,0,0.52)";

      ctx.fillRect(
        0,
        0,
        width,
        height
      );

      ctx.textAlign =
        "center";

      ctx.fillStyle =
        "#ffffff";

      ctx.shadowColor =
        "#ff0000";

      ctx.shadowBlur = 30;

      ctx.font =
        `900 ${Math.min(
          width * 0.12,
          70
        )}px Arial`;

      ctx.fillText(
        "GAME OVER",
        width / 2,
        height * 0.42
      );

      ctx.shadowBlur = 8;

      ctx.fillStyle =
        "#ffd0c5";

      ctx.font =
        "900 24px Arial";

      ctx.fillText(
        `SCORE ${score}`,
        width / 2,
        height * 0.49
      );

      // RESTART BUTTON

      const radius = 16;

      ctx.shadowColor =
        "#36ff6c";

      ctx.shadowBlur = 22;

      ctx.fillStyle =
        "rgba(25,120,45,0.95)";

      ctx.beginPath();

      ctx.rect(
        button.x,
        button.y,
        button.width,
        button.height,
      );

      ctx.fill();

      ctx.lineWidth = 2;

      ctx.strokeStyle =
        "#6dff91";

      ctx.stroke();

      ctx.shadowBlur = 10;

      ctx.fillStyle =
        "#ffffff";

      ctx.font =
        "900 22px Arial";

      ctx.fillText(
        "RESTART",
        width / 2,
        button.y +
          button.height / 2 +
          8
      );

      ctx.restore();
    }

    function update(delta: number) {
      for (
        let i = particles.length - 1;
        i >= 0;
        i--
      ) {
        const p = particles[i];

        p.x += p.vx * delta;
        p.y += p.vy * delta;

        p.vy += 420 * delta;

        p.life -=
          delta / p.maxLife;

        if (p.life <= 0) {
          particles.splice(i, 1);
        }
      }

      for (
        let i =
          hitFlashes.length - 1;
        i >= 0;
        i--
      ) {
        hitFlashes[i].life -=
          delta * 4.5;

        if (
          hitFlashes[i].life <= 0
        ) {
          hitFlashes.splice(
            i,
            1
          );
        }
      }

      for (
        let i =
          scorePops.length - 1;
        i >= 0;
        i--
      ) {
        scorePops[i].life -=
          delta * 2.5;

        if (
          scorePops[i].life <= 0
        ) {
          scorePops.splice(i, 1);
        }
      }

      if (comboPopup) {
        comboPopup.life -=
          delta * 1.7;

        if (comboPopup.life <= 0) {
          comboPopup = null;
        }
      }

      if (winkTime > 0) {
        winkTime -= delta * 0.75;
      }

      if (levelFlash > 0) {
        levelFlash -=
          delta * 1.5;
      }

      if (bombFlash > 0) {
        bombFlash -=
          delta * 3;
      }

      if (!gameStarted) {
        introTime += delta;

        if (introTime >= 2.4) {
          gameStarted = true;
          spawnTimer = 0;
        }

        return;
      }

      if (gameOver) return;

      const width =
        window.innerWidth;

      const height =
        window.innerHeight;

      const gravity =
        height * 1.55;

      spawnTimer += delta;

      const spawnDelay =
        Math.max(
          0.58,
          1.28 -
            level * 0.075
        );

      if (
        spawnTimer >=
        spawnDelay
      ) {
        spawnTimer = 0;
        spawnWave();
      }

      for (const coin of coins) {
        if (coin.sliced) {
          continue;
        }

        coin.vy +=
          gravity * delta;

        coin.x +=
          coin.vx * delta;

        coin.y +=
          coin.vy * delta;

        coin.rotation +=
          coin.rotationSpeed *
          delta;

        if (
          coin.x <
          coin.size / 2
        ) {
          coin.x =
            coin.size / 2;

          coin.vx =
            Math.abs(
              coin.vx
            );
        }

        if (
          coin.x >
          width -
            coin.size / 2
        ) {
          coin.x =
            width -
            coin.size / 2;

          coin.vx =
            -Math.abs(
              coin.vx
            );
        }
      }

      for (
        let i =
          coins.length - 1;
        i >= 0;
        i--
      ) {
        if (
          coins[i].sliced ||
          coins[i].y >
            height +
              coins[i].size * 2
        ) {
          coins.splice(i, 1);
        }
      }
    }

    function gameLoop(now: number) {
      const delta =
        Math.min(
          (now - lastTime) / 1000,
          0.033
        );

      lastTime = now;

      update(delta);

      ctx.clearRect(
        0,
        0,
        window.innerWidth,
        window.innerHeight
      );

      drawBackground();
      drawCoins();
      drawParticles();
      drawHitFlashes();
      drawScorePops();
      drawSlash(now);
      drawHUD();
      drawGameTitle();
      drawCombo();
      drawNinjaCatWink();
      drawLevelFlash();
      drawBombFlash();
      drawGameOver();
      drawIntro();

      animationId =
        requestAnimationFrame(
          gameLoop
        );
    }

    animationId =
      requestAnimationFrame(
        gameLoop
      );

    return () => {
      cancelAnimationFrame(
        animationId
      );

      window.removeEventListener(
        "resize",
        resizeCanvas
      );

      canvas.removeEventListener(
        "pointerdown",
        handlePointerDown
      );

      canvas.removeEventListener(
        "pointermove",
        handlePointerMove
      );

      canvas.removeEventListener(
        "pointerup",
        handlePointerUp
      );

      canvas.removeEventListener(
        "pointercancel",
        handlePointerUp
      );
    };
  }, []);

  return (
    <main className="game-shell">
      <canvas
        ref={canvasRef}
        className="game-canvas"
      />
    </main>
  );
}
